/**
 * AmbientLife — Generic ambient-creature / random-event system.
 *
 * Replaces the single-purpose ambient fish code with a flexible registry
 * where any scene can register ambient "agents" with spawn behaviors.
 *
 * Each AmbientAgent definition describes:
 *   - What sprites/animations to use
 *   - How often to spawn
 *   - Max concurrent on screen
 *   - A behavior factory that knows how to move/destroy a spawned instance
 *
 * Built-in behaviors:
 *   - swimAcross      — linear horizontal pass with bob + rotation wobble
 *   - flyAcross       — arcing flight with wing-flap anim
 *   - driftFloat      — slow vertical drift with sideways sway (jellyfish)
 *   - scuttleGround   — enters from side, scuttles across ground, exits
 *   - shoalSwim       — spawns N minnows as a cohesive group
 *   - arcJump         — rises from bottom, arcs, dives back (flying fish)
 *   - debrisDrift     — slow horizontal drift with spin (boss debris)
 *   - fallFromSky     — diagonal fall across frame (shooting star / soot)
 *
 * Each behavior is a function that receives the scene + sprite + config and
 * sets up the tweens/animations. The system tracks active agents per tag
 * and respawns on a timer.
 */

import Phaser from 'phaser';

// ─── Types ──────────────────────────────────────────────────────────────────

export type AmbientBehavior =
  | 'swimAcross'
  | 'flyAcross'
  | 'driftFloat'
  | 'scuttleGround'
  | 'shoalSwim'
  | 'arcJump'
  | 'debrisDrift'
  | 'fallFromSky'
  | 'stationaryWatcher';

export interface AmbientAgent {
  /** Unique tag for this agent type (used for max-count tracking) */
  tag: string;
  /** Texture keys for frames (first frame used if no animation) */
  textureKeys: string[];
  /** Optional animation key to play on the sprite */
  animKey?: string;
  /** Behavior function used to animate the spawned instance */
  behavior: AmbientBehavior;
  /** Min/max delay between spawns (ms) */
  spawnInterval: { min: number; max: number };
  /** Max concurrent agents of this tag on screen */
  maxConcurrent: number;
  /** Scale range for variety */
  scale: { min: number; max: number };
  /** Alpha range for depth feel */
  alpha: { min: number; max: number };
  /** Depth in the scene render order */
  depth: number;
  /** Optional tint */
  tint?: number;
  /** Optional Y range override for spawning */
  yRange?: { min: number; max: number };
  /** Initial delay before first spawn (ms) */
  initialDelay?: number;
  /** Optional per-agent extras passed to the behavior fn */
  extras?: Record<string, any>;
  /**
   * Optional override for the swim/fly cross-screen duration (ms).
   * When omitted, each behavior uses its built-in default range.
   * Bigger numbers = slower movement. Used to calm the turtle pace
   * below the fast fish baseline.
   */
  durationRange?: { min: number; max: number };
  /**
   * Optional vertical drift applied to swimAcross over the full crossing
   * (in addition to the per-frame bob). Default ±40px keeps the path
   * mostly horizontal. Bias the range negative to drift the agent UP as
   * it crosses (e.g. the turtle sometimes rises diagonally), positive to
   * drift down. Pass a wide range for visible variety per spawn.
   */
  yWanderRange?: { min: number; max: number };
  /**
   * Optional amplitude range for the slow-drift wave layered on top of the
   * stroke bob (swimAcross only). Default 28–46px is a gentle wobble.
   * Bump to e.g. 90–160 for visibly wavy paths — turtle uses this so it
   * undulates noticeably up-and-down while crossing instead of going
   * dead-straight.
   */
  slowBobAmpRange?: { min: number; max: number };
  /**
   * Which way the sprite artwork naturally faces. Behaviors that pick a
   * random horizontal travel direction (swimAcross, flyAcross, etc.) will
   * `setFlipX` so the creature appears to face its movement.
   * Default: 'right' (matches every existing ambient sprite — fish, gulls).
   * Turtle swim frames were generated from a left-facing hero, so it
   * registers as 'left' and the flip logic is inverted for that one.
   */
  spriteBaseFacing?: 'left' | 'right';
}

// ─── System ─────────────────────────────────────────────────────────────────

export class AmbientLife {
  private static instances = new WeakMap<Phaser.Scene, AmbientLife>();

  static get(scene: Phaser.Scene): AmbientLife {
    let inst = this.instances.get(scene);
    if (!inst) {
      inst = new AmbientLife(scene);
      this.instances.set(scene, inst);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        inst!.destroy();
        this.instances.delete(scene);
      });
    }
    return inst;
  }

  private scene: Phaser.Scene;
  private agents: Map<string, { def: AmbientAgent; active: Phaser.GameObjects.GameObject[]; timer?: Phaser.Time.TimerEvent }> = new Map();
  private destroyed = false;

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Register an ambient agent. Starts its spawn loop.
   */
  register(def: AmbientAgent): void {
    if (this.destroyed) return;
    if (this.agents.has(def.tag)) {
      console.warn(`AmbientLife: agent tag "${def.tag}" already registered`);
      return;
    }
    // Skip if no texture keys or any are missing
    if (def.textureKeys.length === 0) {
      console.warn(`AmbientLife: agent "${def.tag}" has no texture keys — skipping`);
      return;
    }
    const missingKeys = def.textureKeys.filter(k => !this.scene.textures.exists(k));
    if (missingKeys.length > 0) {
      console.warn(`AmbientLife: textures missing for agent "${def.tag}": ${missingKeys.join(', ')} — skipping`);
      return;
    }

    console.log(`🐟 AmbientLife: registered "${def.tag}" (behavior=${def.behavior}, max=${def.maxConcurrent})`);
    this.agents.set(def.tag, { def, active: [] });
    const delay = def.initialDelay ?? 1500;
    this.scene.time.delayedCall(delay, () => this.spawnAndSchedule(def.tag));
  }

  /**
   * Remove all agents (used when changing worlds mid-scene).
   */
  clear(): void {
    for (const [, entry] of this.agents) {
      if (entry.timer) entry.timer.remove();
      entry.active.forEach(o => o.destroy());
      entry.active = [];
    }
    this.agents.clear();
  }

  private spawnAndSchedule(tag: string): void {
    const entry = this.agents.get(tag);
    if (!entry || this.destroyed) return;

    // Cull dead agents first
    entry.active = entry.active.filter(a => a.active);

    if (entry.active.length < entry.def.maxConcurrent) {
      this.spawn(entry.def);
    }

    // Schedule next spawn attempt
    const delay = Phaser.Math.Between(entry.def.spawnInterval.min, entry.def.spawnInterval.max);
    entry.timer = this.scene.time.delayedCall(delay, () => this.spawnAndSchedule(tag));
  }

  private spawn(def: AmbientAgent): void {
    const entry = this.agents.get(def.tag);
    if (!entry) return;

    const firstKey = def.textureKeys[Math.floor(Math.random() * def.textureKeys.length)];
    switch (def.behavior) {
      case 'swimAcross':       this.spawnSwimAcross(def, firstKey, entry); break;
      case 'flyAcross':        this.spawnFlyAcross(def, firstKey, entry); break;
      case 'driftFloat':       this.spawnDriftFloat(def, firstKey, entry); break;
      case 'scuttleGround':    this.spawnScuttleGround(def, firstKey, entry); break;
      case 'shoalSwim':        this.spawnShoalSwim(def, firstKey, entry); break;
      case 'arcJump':          this.spawnArcJump(def, firstKey, entry); break;
      case 'debrisDrift':      this.spawnDebrisDrift(def, firstKey, entry); break;
      case 'fallFromSky':      this.spawnFallFromSky(def, firstKey, entry); break;
      case 'stationaryWatcher':this.spawnStationaryWatcher(def, firstKey, entry); break;
    }
  }

  // ─── Behavior implementations ───────────────────────────────────────────

  private makeSprite(def: AmbientAgent, key: string, x: number, y: number): Phaser.GameObjects.Sprite {
    const sprite = this.scene.add.sprite(x, y, key);
    const scale = Phaser.Math.FloatBetween(def.scale.min, def.scale.max);
    sprite.setScale(scale);
    sprite.setAlpha(Phaser.Math.FloatBetween(def.alpha.min, def.alpha.max));
    sprite.setDepth(def.depth);
    if (def.tint !== undefined) sprite.setTint(def.tint);
    if (def.animKey && this.scene.anims.exists(def.animKey)) {
      sprite.play(def.animKey);
    }
    return sprite;
  }

  private track(entry: { active: Phaser.GameObjects.GameObject[] }, sprite: Phaser.GameObjects.GameObject, onDone: () => void): void {
    entry.active.push(sprite);
    (sprite as any).once?.('destroy', onDone);
  }

  /** Fish-style: linear horizontal pass with bob + wobble */
  private spawnSwimAcross(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -100 : w + 100;
    const endX = goingRight ? w + 100 : -100;
    const yRange = def.yRange || { min: 160, max: 600 };
    const y = Phaser.Math.Between(yRange.min, yRange.max);

    const sprite = this.makeSprite(def, key, startX, y);
    // Sprite mirroring: flip when the creature's travel direction doesn't
    // match its natural artwork facing. `spriteBaseFacing` defaults to
    // 'right' for backwards-compat with every pre-existing agent.
    const baseFacing = def.spriteBaseFacing ?? 'right';
    const shouldFlip = baseFacing === 'right' ? !goingRight : goingRight;
    if (shouldFlip) sprite.setFlipX(true);

    const durRange = def.durationRange ?? { min: 4000, max: 7000 };
    const duration = Phaser.Math.Between(durRange.min, durRange.max);
    const yWanderRange = def.yWanderRange ?? { min: -40, max: 40 };
    const yWander = Phaser.Math.Between(yWanderRange.min, yWanderRange.max);

    this.scene.tweens.add({
      targets: sprite,
      x: endX,
      y: y + yWander,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });

    // Primary bob — quick sine wave, matches flipper-stroke cadence.
    this.scene.tweens.add({
      targets: sprite,
      y: `+=${15 + Math.random() * 10}`,
      duration: 800 + Math.random() * 400,
      yoyo: true,
      repeat: Math.ceil(duration / 1600),
      ease: 'Sine.easeInOut',
    });

    // Secondary slow vertical drift — larger amplitude, slower rate.
    // Sells the "drifting with the current" feeling on top of the fast bob.
    const slowAmpRange = def.slowBobAmpRange ?? { min: 28, max: 46 };
    const slowAmp = Phaser.Math.Between(slowAmpRange.min, slowAmpRange.max);
    const slowDur = 2400 + Math.random() * 1200;
    this.scene.tweens.add({
      targets: sprite,
      y: `+=${Math.random() < 0.5 ? slowAmp : -slowAmp}`,
      duration: slowDur,
      yoyo: true,
      repeat: Math.ceil(duration / (slowDur * 2)),
      ease: 'Sine.easeInOut',
    });

    this.scene.tweens.add({
      targets: sprite,
      angle: { from: -3, to: 3 },
      duration: 600 + Math.random() * 400,
      yoyo: true,
      repeat: Math.ceil(duration / 1200),
      ease: 'Sine.easeInOut',
    });

    this.track(entry, sprite, () => { /* tracking cleanup */ });
  }

  /** Bird-style: horizontal pass with wing flap (same as swim but higher Y) */
  private spawnFlyAcross(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -150 : w + 150;
    const endX = goingRight ? w + 150 : -150;
    const yRange = def.yRange || { min: 80, max: 300 };
    const y = Phaser.Math.Between(yRange.min, yRange.max);

    const sprite = this.makeSprite(def, key, startX, y);
    if (!goingRight) sprite.setFlipX(true);

    const duration = Phaser.Math.Between(5000, 9000);

    this.scene.tweens.add({
      targets: sprite,
      x: endX,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });

    // Gentle gliding bob
    this.scene.tweens.add({
      targets: sprite,
      y: `+=${20 + Math.random() * 15}`,
      duration: 1500 + Math.random() * 600,
      yoyo: true,
      repeat: Math.ceil(duration / 3000),
      ease: 'Sine.easeInOut',
    });

    this.track(entry, sprite, () => {});
  }

  /** Jellyfish: slow vertical drift, sideways sway, scale pulse */
  private spawnDriftFloat(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const h = this.scene.cameras.main.height;
    const x = Phaser.Math.Between(100, w - 100);
    const startY = h + 100;
    const endY = -100;

    const sprite = this.makeSprite(def, key, x, startY);

    const duration = Phaser.Math.Between(18000, 28000);

    this.scene.tweens.add({
      targets: sprite,
      y: endY,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });

    // Sideways sway
    this.scene.tweens.add({
      targets: sprite,
      x: `+=${30 + Math.random() * 40}`,
      duration: 3000 + Math.random() * 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Scale pulse (breathing)
    const baseScale = sprite.scaleX;
    this.scene.tweens.add({
      targets: sprite,
      scaleX: baseScale * 1.08,
      scaleY: baseScale * 0.94,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.track(entry, sprite, () => {});
  }

  /** Crab: scuttles across ground with stepping motion */
  private spawnScuttleGround(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -80 : w + 80;
    const endX = goingRight ? w + 80 : -80;
    const y = def.yRange ? Phaser.Math.Between(def.yRange.min, def.yRange.max) : 970;

    const sprite = this.makeSprite(def, key, startX, y);
    if (!goingRight) sprite.setFlipX(true);

    const duration = Phaser.Math.Between(8000, 12000);

    this.scene.tweens.add({
      targets: sprite,
      x: endX,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });

    // Stepping bob — faster than fish
    this.scene.tweens.add({
      targets: sprite,
      y: `-=${8}`,
      duration: 150,
      yoyo: true,
      repeat: Math.ceil(duration / 300),
      ease: 'Sine.easeInOut',
    });

    this.track(entry, sprite, () => {});
  }

  /** Shoal: spawn N small minnows following the same path */
  private spawnShoalSwim(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const count = def.extras?.count || 10;
    const w = this.scene.cameras.main.width;
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -100 : w + 100;
    const endX = goingRight ? w + 100 : -100;
    const yRange = def.yRange || { min: 200, max: 700 };
    const baseY = Phaser.Math.Between(yRange.min, yRange.max);
    const duration = Phaser.Math.Between(6000, 9000);

    for (let i = 0; i < count; i++) {
      const offsetX = (Math.random() - 0.5) * 120;
      const offsetY = (Math.random() - 0.5) * 60;
      const delay = i * 80 + Math.random() * 60;

      const sprite = this.makeSprite(def, key, startX + offsetX, baseY + offsetY);
      if (!goingRight) sprite.setFlipX(true);

      this.scene.time.delayedCall(delay, () => {
        this.scene.tweens.add({
          targets: sprite,
          x: endX + offsetX,
          y: baseY + offsetY + (Math.random() - 0.5) * 40,
          duration,
          ease: 'Linear',
          onComplete: () => sprite.destroy(),
        });
      });

      // Subtle bob
      this.scene.tweens.add({
        targets: sprite,
        y: `+=${6 + Math.random() * 4}`,
        duration: 600 + Math.random() * 300,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      entry.active.push(sprite);
    }
  }

  /** Arcing jump: from water → arc → back into water */
  private spawnArcJump(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? Phaser.Math.Between(100, 400) : Phaser.Math.Between(w - 400, w - 100);
    const waterY = 900;
    const peakX = startX + (goingRight ? 600 : -600);
    const peakY = Phaser.Math.Between(400, 550);
    const endX = startX + (goingRight ? 1200 : -1200);

    const sprite = this.makeSprite(def, key, startX, waterY);
    if (!goingRight) sprite.setFlipX(true);

    // Parabolic path using two tweens
    this.scene.tweens.add({
      targets: sprite,
      x: peakX,
      y: peakY,
      duration: 600,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: sprite,
          x: endX,
          y: waterY,
          duration: 600,
          ease: 'Sine.easeIn',
          onComplete: () => sprite.destroy(),
        });
      },
    });

    // Tilt with arc
    this.scene.tweens.add({
      targets: sprite,
      angle: { from: goingRight ? -20 : 20, to: goingRight ? 20 : -20 },
      duration: 1200,
      ease: 'Sine.easeInOut',
    });

    this.track(entry, sprite, () => {});
  }

  /** Debris drifting slowly across screen with spin */
  private spawnDebrisDrift(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -80 : w + 80;
    const endX = goingRight ? w + 80 : -80;
    const yRange = def.yRange || { min: 400, max: 900 };
    const y = Phaser.Math.Between(yRange.min, yRange.max);

    const sprite = this.makeSprite(def, key, startX, y);
    const duration = Phaser.Math.Between(10000, 16000);

    this.scene.tweens.add({
      targets: sprite,
      x: endX,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });

    // Slow rotation
    this.scene.tweens.add({
      targets: sprite,
      angle: (Math.random() > 0.5 ? 360 : -360),
      duration: duration,
      ease: 'Linear',
    });

    // Bobbing
    this.scene.tweens.add({
      targets: sprite,
      y: `+=${10}`,
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.track(entry, sprite, () => {});
  }

  /** Falling diagonally (shooting star, falling soot) */
  private spawnFallFromSky(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;
    const h = this.scene.cameras.main.height;
    const startX = Phaser.Math.Between(0, w);
    const startY = -50;
    const endX = startX + Phaser.Math.Between(-200, 400);
    const endY = h + 50;

    const sprite = this.makeSprite(def, key, startX, startY);
    const duration = Phaser.Math.Between(1500, 3000);

    this.scene.tweens.add({
      targets: sprite,
      x: endX,
      y: endY,
      duration,
      ease: 'Linear',
      onComplete: () => sprite.destroy(),
    });

    this.track(entry, sprite, () => {});
  }

  /**
   * Stationary creature that sits at a fixed spawn point and plays its
   * idle animation — used for "audience" crabs watching the fight.
   * Fires a subtle emote bob every few seconds for liveliness.
   * Uses extras.x and extras.y for exact position, else picks a random
   * ground-edge corner.
   */
  private spawnStationaryWatcher(def: AmbientAgent, key: string, entry: { active: Phaser.GameObjects.GameObject[] }): void {
    const w = this.scene.cameras.main.width;

    // Use explicit position if provided, otherwise use a fixed right-side
    // spot that's clear of the center seaweed foreground
    let x: number;
    let y: number;
    if (def.extras?.x !== undefined && def.extras?.y !== undefined) {
      x = def.extras.x as number;
      y = def.extras.y as number;
    } else {
      // Fixed right-side position on bare sand — far enough right to
      // avoid the center-left seaweed
      x = w - 90; // ~1830 on a 1920-wide canvas
      y = def.yRange ? Phaser.Math.Between(def.yRange.min, def.yRange.max) : 990;
    }

    const sprite = this.makeSprite(def, key, x, y);
    // Face toward the center of the screen (the fight)
    if (x > w / 2) sprite.setFlipX(true);

    // Subtle ground shadow — flat ellipse beneath the watcher so it reads as
    // sitting on the seafloor rather than floating. Scaled to ~70% of the
    // sprite's display width and breathes gently in sync with the bob.
    const shadowW = Math.max(40, Math.round(sprite.displayWidth * 0.7));
    const shadowH = Math.max(8, Math.round(shadowW * 0.18));
    const shadow = this.scene.add.ellipse(
      x,
      y + sprite.displayHeight * 0.42,
      shadowW,
      shadowH,
      0x000000,
      0.28,
    );
    shadow.setDepth(sprite.depth - 0.1);
    this.scene.tweens.add({
      targets: shadow,
      scaleX: { from: 0.95, to: 1.05 },
      alpha: { from: 0.22, to: 0.32 },
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    sprite.once('destroy', () => shadow.destroy());

    // Subtle idle bob (2px up-down)
    this.scene.tweens.add({
      targets: sprite,
      y: y - 2,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Random "emote" — occasional excited bounce like cheering
    const emote = this.scene.time.addEvent({
      delay: Phaser.Math.Between(2500, 5000),
      loop: true,
      callback: () => {
        if (!sprite.active) { emote.remove(); return; }
        this.scene.tweens.add({
          targets: sprite,
          y: y - 12,
          scaleY: sprite.scaleY * 1.1,
          duration: 150,
          yoyo: true,
          ease: 'Sine.easeOut',
        });
      },
    });

    // Track — these live until the scene shuts down
    this.track(entry, sprite, () => {
      emote.remove();
    });

    // Clean up emote when sprite dies
    sprite.once('destroy', () => emote.remove());
  }

  private destroy(): void {
    this.destroyed = true;
    this.clear();
  }
}
